import { useRef, useCallback } from "react";

/**
 * @typedef {{ tagMap: Map<string, Object>, udMap: Map<number, Object>, sourceTagData: Object }} ParameterMapsEntry
 */

/**
 * Index tag parameters by `parameter` name (tag name) for O(1) lookup.
 *
 * Walks every category array in transformed tag data and maps each item's
 * `param.parameter` string to the full parameter object. Parameters without
 * a truthy `parameter` field are skipped. If the same name appears more than
 * once, the last occurrence wins (unlike {@link buildUniversalDescriptorParameterMapFromTagData},
 * which keeps the first UD per id).
 *
 * Built alongside the UD map in {@link useOperatingPointsParameterMap}'s cache
 * (`ParameterMapsEntry.tagMap`). Operating Points measurement columns resolve
 * values via `udMap` today; `tagMap` supports tag-name lookups on the same cache entry.
 *
 * @param {Object} tagData - Transformed tag data (categories → parameter arrays)
 * @returns {Map<string, Object>} Map from parameter/tag name to parameter object
 */
function buildTagParameterMapFromTagData(tagData) {
  const tagMap = new Map();
  Object.values(tagData).forEach((category) => {
    if (!Array.isArray(category)) return;
    category.forEach((param) => {
      if (param.parameter) {
        tagMap.set(param.parameter, param);
      }
    });
  });
  return tagMap;
}

/**
 * Index parameters by UniversalDescriptorId (first occurrence wins per id).
 *
 * @param {Object} tagData - Transformed tag data (categories → parameter arrays)
 * @returns {Map<number, Object>}
 */
function buildUniversalDescriptorParameterMapFromTagData(tagData) {
  const udMap = new Map();
  Object.values(tagData).forEach((category) => {
    if (!Array.isArray(category)) return;
    category.forEach((param) => {
      const raw = param.universalDescriptorId ?? param.UniversalDescriptorId;
      if (raw === undefined || raw === null) return;
      if (typeof raw === "string" && raw.trim() === "") return;
      const id = Number(raw);
      if (!Number.isFinite(id)) return;
      if (!udMap.has(id)) {
        udMap.set(id, param);
      }
    });
  });
  return udMap;
}

/**
 * Custom hook for managing parameter maps for fast Universal Descriptor lookups.
 * @returns {Object} { getUniversalDescriptorParameterMap, parameterMapsRef }
 */
export function useOperatingPointsParameterMap() {
  const parameterMapsRef = useRef(
    /** @type {Map<string, ParameterMapsEntry>} */ (new Map())
  );

  /**
   * Build or return cached maps for an asset.
   * Cache is invalidated when `tagData` is replaced (new object reference after refetch).
   * @param {string} assetId
   * @param {Object} tagData
   * @returns {ParameterMapsEntry|null}
   */
  const getOrBuildMaps = useCallback((assetId, tagData) => {
    if (!assetId || !tagData) return null;

    const existing = parameterMapsRef.current.get(assetId);
    if (existing && existing.sourceTagData === tagData) return existing;

    const tagMap = buildTagParameterMapFromTagData(tagData);
    const udMap = buildUniversalDescriptorParameterMapFromTagData(tagData);

    const entry = { tagMap, udMap, sourceTagData: tagData };
    parameterMapsRef.current.set(assetId, entry);
    return entry;
  }, []);

  /**
   * @param {string} assetId - Asset ID
   * @param {Object} tagData - Tag data object
   * @returns {Map<number, Object>|null} Parameter map keyed by UniversalDescriptorId
   */
  const getUniversalDescriptorParameterMap = useCallback(
    (assetId, tagData) => {
      const entry = getOrBuildMaps(assetId, tagData);
      return entry?.udMap ?? null;
    },
    [getOrBuildMaps]
  );

  return {
    getUniversalDescriptorParameterMap,
    parameterMapsRef,
  };
}
